import React, { useMemo } from 'react';
import classNames from 'classnames';
import Sentiment from '@op/components/Snippets/Sentiment';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_V } from '@op/utils/format';
import { arrayToObj, getTimeSpaceArray } from '@op/utils';
import StatusBadges from '../StatusBadges';
import { IDiscussionItem, METRIC_CONFIG, ITrendPoint } from '../types';
import './index.scss';
import { getEventTitleTextGlobal } from '@op/pages/Summary/utils';
import TableMiniChart from '@opm/components/TableMiniChart';
import { Justify } from 'tea-component';
import CommonBubble from '@home/components/CommonBubble';

interface DiscussionItemProps {
  item: IDiscussionItem;
  isSelected?: boolean;
  onClick?: (item: IDiscussionItem) => void;
  activeKey?: string;
  /** 当前事件的趋势图数据 */
  trendData?: ITrendPoint[];
  /** 趋势图加载状态 */
  trendLoading?: boolean;
  /** API 返回的趋势时间范围开始（批次级别，同一批次的全部事件共用） */
  timelineStartTime?: string;
  /** API 返回的趋势时间范围结束（批次级别，同一批次的全部事件共用） */
  timelineEndTime?: string;
}

/** 单个讨论项，支持列表交互模式和纯展示模式 */
export default function DiscussionItem({
  item,
  isSelected = false,
  onClick,
  activeKey,
  trendData,
  trendLoading,
  timelineStartTime,
  timelineEndTime,
}: DiscussionItemProps) {
  const { isEn, formatMessage } = useLanguage();

  // ─── 趋势图数据适配：ITrendPoint[] → TableMiniChart 格式 ───
  // 优先使用 API 返回的真实时间范围，回退到从数据首尾推导
  // 通过 getTimeSpaceArray 生成完整日期轴，arrayToObj 映射填充零值，确保缺失日期也能正常展示
  const chartData = useMemo(() => {
    if (!trendData || trendData.length === 0) return undefined;

    const startTime = timelineStartTime || trendData[0].date;
    const endTime = timelineEndTime || trendData[trendData.length - 1].date;

    // 构建完整日期序列（含所有日期，包括无数据的天）
    const fullTimeList = getTimeSpaceArray(startTime, endTime);

    // 将后台趋势数据按 date 映射，便于快速查找
    const trendMap = arrayToObj(trendData, (d: ITrendPoint) => d.date);
    return {
      timeline_of_start_time: startTime,
      timeline_of_end_time: endTime,
      timeline: fullTimeList.map((time: string) => ({
        time,
        value: trendMap?.[time]?.mentions ?? 0,
      })),
    };
  }, [trendData, timelineStartTime, timelineEndTime]);

  const className = classNames('dbo-discussion-item', {
    'is-selected': isSelected && !!onClick,
  });

  return (
    <div
      className={className}
      onClick={onClick ? () => onClick(item) : undefined}
    >
      <Justify
        left={
          <>
            {/* 第一行：情感分图标 + 标题 + 状态标签 */}
            <div className="dbo-discussion-item-row">
              <Sentiment
                className="dbo-discussion-item-sentiment"
                sentiment={item.avg_sentiment}
                showValue={false}
              />
              <span className="dbo-discussion-item-title">
                {getEventTitleTextGlobal(item, isEn)}
              </span>
              <StatusBadges item={item} />
            </div>

            {/* 第二行：各项指标 */}
            <div className="dbo-discussion-item-metrics">
              {METRIC_CONFIG.map((metric) => (
                <CommonBubble key={metric.key} content={formatMessage(metric.label || metric.labelKey)}>
                  <span
                    className={classNames(
                      'dbo-discussion-item-metric',
                      `dbo-discussion--${metric.labelKey}`,
                      {
                        'dbo-discussion-item-metric--active':
                          metric.key === activeKey,
                      },
                    )}
                  >
                    <span className="dbo-discussion-item-metric-label">
                      {/* {formatMessage({ id: metric.labelKey })} */}
                    </span>
                    <span className="dbo-discussion-item-metric-value">
                      {DBO_V.reviews(Number(item[metric.key]) || 0)}
                    </span>
                  </span>
                </CommonBubble>
              ))}
            </div>
          </>
        }
        right={
          chartData && (
            <TableMiniChart
              data={chartData}
              name={formatMessage('Mentions')}
              height={60}
              showTooltip={true}
              tooltipModel={"two"}
              filterEmptyValue={false}
              timeFormat="daily"
            />
          )
        }
      ></Justify >
    </div >
  );
}
